import { Fragment, type ReactNode } from 'react';

// A small, safe Markdown renderer for what the agents write: headings, paragraphs, bullet and numbered lists, bold,
// italic, inline code, links (http only), tables, rules and quotes. It builds React elements, never HTML, so a draft
// can never inject markup. Sections are recognised by their heading so the page can give each the right shape:
// an email reads like an email, reviewer notes like a callout, gaps like a checklist of what is missing.
type Block = { kind: 'h'; level: number; text: string } | { kind: 'p'; text: string } | { kind: 'ul' | 'ol'; items: string[] } | { kind: 'hr' } | { kind: 'quote'; text: string } | { kind: 'table'; rows: string[][] } | { kind: 'code'; text: string };

function parse(src: string): Block[] {
  const lines = src.replace(/\r/g, '').split('\n'); const out: Block[] = []; let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    const h = line.match(/^(#{1,6})\s+(.*)$/); if (h) { out.push({ kind: 'h', level: h[1].length, text: h[2].trim() }); i++; continue; }
    if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) { out.push({ kind: 'hr' }); i++; continue; }
    if (line.startsWith('```')) { const buf: string[] = []; i++; while (i < lines.length && !lines[i].startsWith('```')) buf.push(lines[i++]); i++; out.push({ kind: 'code', text: buf.join('\n') }); continue; }
    if (/^\s*>/.test(line)) { const buf: string[] = []; while (i < lines.length && /^\s*>/.test(lines[i])) buf.push(lines[i++].replace(/^\s*>\s?/, '')); out.push({ kind: 'quote', text: buf.join('\n') }); continue; }
    if (/^\s*\|.*\|\s*$/.test(line)) { const rows: string[][] = []; while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) { const cells = lines[i].trim().slice(1, -1).split('|').map((c) => c.trim()); if (!cells.every((c) => /^:?-{2,}:?$/.test(c))) rows.push(cells); i++; } out.push({ kind: 'table', rows }); continue; }
    const bullet = /^\s*[-*•]\s+/; const num = /^\s*\d+[.)]\s+/;
    if (bullet.test(line) || num.test(line)) {
      const ordered = num.test(line); const re = ordered ? num : bullet; const items: string[] = [];
      while (i < lines.length && (re.test(lines[i]) || (lines[i].trim() && /^\s{2,}/.test(lines[i]) && items.length))) { if (re.test(lines[i])) items.push(lines[i].replace(re, '').trim()); else items[items.length - 1] += ' ' + lines[i].trim(); i++; }
      out.push({ kind: ordered ? 'ol' : 'ul', items }); continue;
    }
    const buf: string[] = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,6})\s/.test(lines[i]) && !bullet.test(lines[i]) && !num.test(lines[i]) && !/^\s*>/.test(lines[i]) && !/^\s*\|.*\|\s*$/.test(lines[i]) && !lines[i].startsWith('```') && !/^(-{3,}|\*{3,})\s*$/.test(lines[i])) buf.push(lines[i++]);
    out.push({ kind: 'p', text: buf.join('\n') });
  }
  return out;
}

/** Inline marks: **bold**, *italic*, `code`, [text](http link). Merge fields such as {{first_name}} are shown as chips. */
export function inline(text: string): ReactNode[] {
  const out: ReactNode[] = []; let rest = text; let k = 0;
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\((https?:\/\/[^)\s]+)\)|\{\{[a-z_]+\}\}|\*[^*\n]+\*)/;
  while (rest.length) {
    const m = rest.match(re);
    if (!m || m.index == null) { out.push(rest); break; }
    if (m.index > 0) out.push(rest.slice(0, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) out.push(<strong key={k++}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith('`')) out.push(<code key={k++}>{tok.slice(1, -1)}</code>);
    else if (tok.startsWith('[')) { const t = tok.slice(1, tok.indexOf(']')); out.push(<a key={k++} href={m[2]} target="_blank" rel="noreferrer">{t}</a>); }
    else if (tok.startsWith('{{')) out.push(<span key={k++} className="merge">{tok}</span>);
    else out.push(<em key={k++}>{tok.slice(1, -1)}</em>);
    rest = rest.slice(m.index + tok.length);
  }
  return out;
}

const sectionClass = (title: string) => {
  const t = title.toLowerCase();
  if (/^at a glance/.test(t)) return 'glance';
  if (/reviewer notes|read first|what changed/.test(t)) return 'notes';
  if (/^email|^subject|follow-up email|reply-thread email|bump/.test(t)) return 'email';
  if (/call script|call block|opener \(/.test(t)) return 'script';
  if (/^gaps|not in the context|could not verify|needs sourcing|left out/.test(t)) return 'gaps';
  if (/linkedin/.test(t)) return 'social';
  return '';
};

/** Render Markdown as sections: each ## heading opens a card whose look follows what it is. */
export function Markdown({ text, sections = true }: { text?: string | null; sections?: boolean }) {
  if (!text) return null;
  const blocks = parse(text);
  // Drop a lone leading H1: the page already carries the title.
  if (blocks[0]?.kind === 'h' && blocks[0].level === 1) blocks.shift();
  const render = (b: Block, i: number): ReactNode => {
    switch (b.kind) {
      case 'h': return b.level <= 2 ? <h3 key={i}>{inline(b.text)}</h3> : <h4 key={i}>{inline(b.text)}</h4>;
      case 'p': return <p key={i}>{b.text.split('\n').map((l, j, arr) => <Fragment key={j}>{inline(l)}{j < arr.length - 1 && <br />}</Fragment>)}</p>;
      case 'ul': return <ul key={i}>{b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}</ul>;
      case 'ol': return <ol key={i}>{b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}</ol>;
      case 'hr': return <hr key={i} />;
      case 'quote': return <blockquote key={i}>{b.text.split('\n').map((l, j) => <p key={j}>{inline(l)}</p>)}</blockquote>;
      case 'code': return <pre key={i} className="snippet">{b.text}</pre>;
      case 'table': return <div key={i} className="table-wrap"><table><tbody>{b.rows.map((r, j) => <tr key={j}>{r.map((c, k) => j === 0 ? <th key={k}>{inline(c)}</th> : <td key={k}>{inline(c)}</td>)}</tr>)}</tbody></table></div>;
    }
  };
  if (!sections) return <div className="md">{blocks.map(render)}</div>;
  // Group into sections at each level-1/2 heading.
  const groups: Array<{ title: string | null; cls: string; blocks: Block[] }> = [];
  for (const b of blocks) {
    if (b.kind === 'h' && b.level <= 2) groups.push({ title: b.text, cls: sectionClass(b.text), blocks: [] });
    else { if (!groups.length) groups.push({ title: null, cls: '', blocks: [] }); groups[groups.length - 1].blocks.push(b); }
  }
  return (
    <div className="md sections">
      {groups.map((g, gi) => (
        <section key={gi} className={`sec ${g.cls}`}>
          {g.title && <h3>{inline(g.title)}</h3>}
          {g.cls === 'email' ? <EmailBody blocks={g.blocks} render={render} /> : g.blocks.map(render)}
        </section>
      ))}
    </div>
  );
}

/** An email section: the Subject line becomes a header row, the rest reads like the message itself. */
function EmailBody({ blocks, render }: { blocks: Block[]; render: (b: Block, i: number) => ReactNode }) {
  let subject: string | null = null; const rest: Block[] = [];
  for (const b of blocks) {
    if (subject === null && b.kind === 'p') { const m = b.text.match(/^\**Subject:?\**\s*:?\s*(.+)$/im); if (m) { subject = m[1].replace(/\*\*/g, '').trim(); const other = b.text.split('\n').filter((l) => !/^\**Subject/i.test(l)).join('\n').trim(); if (other) rest.push({ kind: 'p', text: other }); continue; } }
    rest.push(b);
  }
  return (<>
    {subject && <div className="subject"><span>Subject</span><b>{inline(subject)}</b></div>}
    <div className="message">{rest.map(render)}</div>
  </>);
}
